import React, { useEffect, useState, useRef, useCallback, memo, useMemo } from 'react';
import { CheckCircle2, XCircle, AlertCircle, AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface NotificationProps {
  type?: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message?: string;
  onClose: () => void;
  duration?: number;
  /** Called when the toast body is clicked (e.g. navigate to the chat it announces). */
  onClick?: () => void;
}

const NotificationComponent = memo(function Notification({
  type = 'success',
  title,
  message,
  onClose,
  duration = 5000,
  onClick,
}: NotificationProps) {
  const [isClosing, setIsClosing] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const animationTimeoutRef = useRef<NodeJS.Timeout>();

  const handleClose = useCallback(() => {
    if (isClosing) return; // Prevent multiple calls

    setIsClosing(true);
    animationTimeoutRef.current = setTimeout(() => {
      onClose();
    }, 300); // Match the animation duration
  }, [isClosing, onClose]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleClose();
    }
  }, [handleClose]);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [duration, handleClose]);

  // Memoize static configurations to prevent re-creation on every render
  const icons = useMemo(() => ({
    success: <CheckCircle2 className="h-5 w-5 text-green-500" />,
    error: <XCircle className="h-5 w-5 text-red-500" />,
    info: <AlertCircle className="h-5 w-5 text-blue-500" />,
    warning: <AlertTriangle className="h-5 w-5 text-yellow-500" />,
  }), []);

  const backgrounds = useMemo(() => ({
    success: 'bg-green-50 border-green-200',
    error: 'bg-red-50 border-red-200',
    info: 'bg-blue-50 border-blue-200',
    warning: 'bg-yellow-50 border-yellow-200',
  }), []);

  const titles = useMemo(() => ({
    success: 'text-green-800',
    error: 'text-red-800',
    info: 'text-blue-800',
    warning: 'text-yellow-800',
  }), []);

  const messages = useMemo(() => ({
    success: 'text-green-700',
    error: 'text-red-700',
    info: 'text-blue-700',
    warning: 'text-yellow-700',
  }), []);

  const handleBodyClick = useCallback(() => {
    if (!onClick) return;
    onClick();
    handleClose();
  }, [onClick, handleClose]);

  return (
    <div
      className={cn(
        'relative rounded-lg border shadow-lg w-full max-w-sm',
        'p-4 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2',
        'transform transition-all duration-300 ease-out',
        'will-change-transform', // Optimize for animations
        isClosing ? 'animate-slide-out' : 'animate-slide-in',
        onClick && 'cursor-pointer',
        backgrounds[type]
      )}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      onClick={handleBodyClick}
      // The inline `zIndex: 10000 + (Date.now() % 1000)` that used to live here gave each
      // notification a RANDOM z-index in the 10000-10999 band — nondeterministic stacking
      // against itself and against the 10000 modal layer. Stacking is the container's job
      // (it sets a z-index per item, below); this element does not need one at all.
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0" aria-hidden="true">
          {icons[type]}
        </div>
        <div className="flex-1">
          <h3
            className={cn('text-sm font-medium', titles[type])}
            id={`notification-title-${Math.random().toString(36).substring(7)}`}
          >
            {title}
          </h3>
          {message && (
            <p
              className={cn('mt-1 text-sm', messages[type])}
              id={`notification-message-${Math.random().toString(36).substring(7)}`}
            >
              {message}
            </p>
          )}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); handleClose(); }}
          className={cn(
            'flex-shrink-0 rounded-lg p-1 transition-colors touch-manipulation',
            // 16px icon + p-1 = a 24px dismiss target, under the 44pt minimum. Same
            // treatment as ToastClose: grow the HIT AREA with a transparent
            // pseudo-element (24 + 2*10 = 44) so the painted X is unchanged. Mobile-only.
            "max-[937px]:relative max-[937px]:after:absolute max-[937px]:after:inset-x-[-10px] max-[937px]:after:inset-y-[-10px] max-[937px]:after:content-['']",
            'text-gray-500 hover:text-gray-700 hover:bg-white/25',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
            'focus:ring-offset-transparent'
          )}
          aria-label={`Dismiss ${type} notification: ${title}`}
          type="button"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
});

export const Notification = NotificationComponent;

interface NotificationContainerProps {
  notifications: Array<{
    id: string;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message?: string;
    url?: string;
  }>;
  onClose: (id: string) => void;
  onNavigate?: (url: string) => void;
}

export const NotificationContainer = memo(function NotificationContainer({
  notifications,
  onClose,
  onNavigate
}: NotificationContainerProps) {
  // Memoize the close handlers to prevent unnecessary re-renders
  const closeHandlers = useMemo(() => {
    const handlers = new Map();
    notifications.forEach(notification => {
      handlers.set(notification.id, () => onClose(notification.id));
    });
    return handlers;
  }, [notifications, onClose]);

  // Don't render container if no notifications
  if (notifications.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed notification-container",
        // Desktop positioning
        "min-[938px]:top-4 min-[938px]:right-4 min-[938px]:w-96",
        // Mobile - clear the fixed bottom nav. This offset was declared TWICE in this same
        // class list (80px and a 100px "iPhone specific" variant), leaving tailwind-merge
        // to arbitrate; neither matched the real nav height. One declaration now, against
        // the nav's own variable, which already includes env(safe-area-inset-bottom) and
        // matches what mobile-layout.css sets for `.notification-container`.
        "max-[937px]:bottom-[calc(var(--mobile-nav-total-height)+1rem)]",
        "max-[937px]:left-4 max-[937px]:right-4"
      )}
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      style={{
        // 10050, not 10000: AlertDialog's overlay and content are both z-[10000] root-level
        // stacking contexts and their portal mounts later, so a tied notification painted
        // under the alert's bg-black/80 and was invisible. Inline (rather than a class)
        // because mobile-layout.css pins `.notification-container` to
        // var(--z-notifications) = 1080, which would otherwise bury it under every modal.
        zIndex: 10050,
      }}
    >
      <div className="space-y-3 pointer-events-auto">
        {notifications.map((notification, index) => (
          <div
            key={notification.id}
            className={cn(
              'transform transition-all duration-200 ease-out',
              index > 0 && 'animate-stack-slide-in'
            )}
            style={{
              // Stagger z-index for proper layering (kept in step with the container's
              // 10050, which now sits above the 10000 modal layer)
              zIndex: 10050 - index,
            }}
          >
            <Notification
              type={notification.type}
              title={notification.title}
              message={notification.message}
              onClose={closeHandlers.get(notification.id)!}
              onClick={notification.url && onNavigate
                ? () => onNavigate(notification.url!)
                : undefined}
            />
          </div>
        ))}
      </div>
    </div>
  );
});